/**
 * Transaction Confirmation Dialog
 *
 * Displays decoded transaction instructions for user review before signing.
 * Hot wallet only — Ledger has device-level confirmation.
 *
 * Features:
 * - Instruction list with program name badges (green/yellow/red)
 * - Expandable instruction args and accounts
 * - Unknown program detection with CONFIRM text gate
 * - 60-second countdown timer
 */

import { useState, useEffect, useCallback } from "react";
import { Shield, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useTransactionGuardStore } from "../store/transaction-guard-store";
import type {
  DecodedInstruction,
  DecodedAccount,
} from "../lib/transaction-decoder";
import { INSTRUCTION_REGISTRY } from "@/lib/solana/instruction-registry";
import { WELL_KNOWN_PROGRAMS } from "@/lib/solana/instruction-registry/well-known-programs";

const TIMEOUT_MS = 60_000;
const COUNTDOWN_SHOW_THRESHOLD = 15;

/** Determine badge variant for a program */
function getProgramBadgeStyle(
  programId: string,
  isKnown: boolean,
): { className: string; label: string } {
  if (!isKnown) {
    return {
      className: "bg-red-100 text-red-800 border-red-200",
      label: "Unknown",
    };
  }

  // Check if it's a registered program (in INSTRUCTION_REGISTRY)
  if (INSTRUCTION_REGISTRY.has(programId)) {
    return {
      className: "bg-green-100 text-green-800 border-green-200",
      label: "Registered",
    };
  }

  // Check if it's a well-known system program
  if (WELL_KNOWN_PROGRAMS.has(programId)) {
    return {
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "System",
    };
  }

  return {
    className: "bg-gray-100 text-gray-800 border-gray-200",
    label: "Other",
  };
}

/** Truncate address for display */
function truncateAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

/** Single instruction card */
function InstructionCard({ ix }: { ix: DecodedInstruction }) {
  const [expanded, setExpanded] = useState(false);
  const badge = getProgramBadgeStyle(ix.programId, ix.isKnownProgram);
  const hasDetails = ix.args.length > 0 || ix.accounts.length > 0;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={cn(
          "w-full flex items-center gap-3 p-3 text-left",
          hasDetails && "hover:bg-gray-50 cursor-pointer",
          !hasDetails && "cursor-default",
        )}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={cn("text-xs", badge.className)}>
              {badge.label}
            </Badge>
            <span className="text-sm font-medium text-gray-900">
              {ix.programName}
            </span>
          </div>
          <p className="text-sm text-gray-700 font-medium">
            {ix.instructionName}
          </p>
          {ix.description && (
            <p className="text-xs text-gray-500 mt-0.5">{ix.description}</p>
          )}
        </div>
        {hasDetails && (
          <div className="flex-shrink-0 text-gray-400">
            {expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </div>
        )}
      </button>

      {expanded && hasDetails && (
        <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-3">
          {/* Arguments */}
          {ix.args.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">
                Arguments
              </p>
              <div className="space-y-1">
                {ix.args.map((arg) => (
                  <div
                    key={arg.name}
                    className="flex items-baseline gap-2 text-xs"
                  >
                    <span className="text-gray-500 min-w-[100px]">
                      {arg.label}:
                    </span>
                    <span className="text-gray-900 font-mono break-all">
                      {arg.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Accounts */}
          {ix.accounts.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">
                Accounts ({ix.accounts.length})
              </p>
              <div className="space-y-1">
                {ix.accounts.map((acc: DecodedAccount, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-gray-700">
                      {truncateAddress(acc.pubkey)}
                    </span>
                    {acc.isSigner && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-blue-50 text-blue-700 border-blue-200"
                      >
                        signer
                      </Badge>
                    )}
                    {acc.isWritable && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-orange-50 text-orange-700 border-orange-200"
                      >
                        writable
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Main dialog component */
export function TransactionConfirmationDialog() {
  const {
    pendingTransaction,
    decodedTransaction,
    confirmTransaction,
    rejectTransaction,
  } = useTransactionGuardStore();

  const [confirmText, setConfirmText] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(TIMEOUT_MS / 1000);

  const isOpen = pendingTransaction !== null && decodedTransaction !== null;
  const hasUnknownPrograms = (decodedTransaction?.warnings.length ?? 0) > 0;

  // Countdown timer
  useEffect(() => {
    if (!isOpen) {
      setSecondsRemaining(TIMEOUT_MS / 1000);
      setConfirmText("");
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, Math.ceil((TIMEOUT_MS - elapsed) / 1000));
      setSecondsRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    confirmTransaction();
  }, [confirmTransaction]);

  const handleCancel = useCallback(() => {
    rejectTransaction();
  }, [rejectTransaction]);

  if (!isOpen || !decodedTransaction) return null;

  const canConfirm = hasUnknownPrograms ? confirmText === "CONFIRM" : true;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-6 h-6 text-blue-600" />
            Review Transaction
          </DialogTitle>
          <DialogDescription>
            Review the transaction details before signing
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Warnings */}
          {hasUnknownPrograms && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Unknown Programs Detected
                  </p>
                  <ul className="text-xs text-red-700 mt-1 space-y-0.5">
                    {decodedTransaction.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Instructions ({decodedTransaction.instructions.length})
            </p>
            {decodedTransaction.instructions.map((ix) => (
              <InstructionCard key={ix.index} ix={ix} />
            ))}
          </div>

          {/* Fee payer */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">Fee Payer</p>
            <p className="text-sm font-mono text-gray-900">
              {truncateAddress(decodedTransaction.feePayer)}
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-3">
          {/* Unknown program confirmation gate */}
          {hasUnknownPrograms && (
            <div className="w-full space-y-2">
              <p className="text-sm text-red-600 font-medium">
                This transaction contains unknown programs. Type CONFIRM to
                proceed.
              </p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type CONFIRM"
                className="font-mono"
              />
            </div>
          )}

          <div className="flex items-center justify-between w-full gap-2">
            {/* Countdown timer */}
            <div className="text-xs text-gray-400">
              {secondsRemaining <= COUNTDOWN_SHOW_THRESHOLD && (
                <span className="text-amber-600 font-medium">
                  {secondsRemaining}s remaining
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!canConfirm}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Confirm & Sign
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
